import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Redo2, Undo2 } from '../../ui/Icon';
import type { ItineraryHistory } from '../../../hooks/useItineraryHistory';

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

/**
 * Undo/redo buttons for the plan, and their shortcuts: Ctrl/⌘+Z, and
 * Ctrl/⌘+Shift+Z or Ctrl+Y. Mounted by the views that edit the plan, so the
 * shortcut never changes the itinerary from a tab where it isn't on screen.
 */
export function UndoRedoControls({ history }: { history: ItineraryHistory }) {
  const { t } = useTranslation();
  const { undo, redo } = history;

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (!(e.metaKey || e.ctrlKey) || e.altKey || isTyping(e.target)) return;
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) { e.preventDefault(); void undo(); return; }
      if ((key === 'z' && e.shiftKey) || (key === 'y' && e.ctrlKey)) { e.preventDefault(); void redo(); }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [undo, redo]);

  const button = 'p-1.5 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-default disabled:hover:bg-transparent';

  return (
    <div className="flex items-center gap-0.5 shrink-0">
      <button
        type="button"
        onClick={() => { void undo(); }}
        disabled={!history.canUndo || history.busy}
        aria-label={t('itinerary.undo.undoLabel')}
        title={t('itinerary.undo.undoLabel')}
        className={button}
      >
        <Icon as={Undo2} size={16} />
      </button>
      <button
        type="button"
        onClick={() => { void redo(); }}
        disabled={!history.canRedo || history.busy}
        aria-label={t('itinerary.undo.redoLabel')}
        title={t('itinerary.undo.redoLabel')}
        className={button}
      >
        <Icon as={Redo2} size={16} />
      </button>
    </div>
  );
}
